/**
 * What the special kinds are, and the few things every module needs to ask or do about
 * them. A leaf: `hazards/blast.ts` (called from `damage.ts`) imports this file, so it must
 * not import `damage.ts`, the state machine or anything that does.
 */

import type { Ctx } from '../../ctx';
import { ENEMY, SPECIALS, VEHICLES } from '../../tuning';
import { type Enemy, type EnemyKind, type FuelTank, type HazardState, TICK_RATE } from '../../types';

const VEHICLE_KINDS: readonly EnemyKind[] = ['truck', 'staffCar', 'railEngine', 'launcherTruck'];

/** A machine that drives a route (ROUND4 §1): a lorry, a staff car, the train, a launcher under way. */
export function isVehicle(kind: EnemyKind): boolean {
  return VEHICLE_KINDS.includes(kind);
}

/** Machines: no state machine, no temperament, no detection meter; armour only a heavy blast cracks. */
/** ROUND8: a tracker dog. Not a machine and not a man: it runs its own step (`./dog.ts`). */
export function isDog(kind: EnemyKind): boolean {
  return kind === 'dog';
}

export function isAutomated(kind: EnemyKind): boolean {
  return kind === 'miniBunker' || kind === 'minitank' || isVehicle(kind);
}

/**
 * The body a projectile has to strike, as wide as the MODEL is (02 §7). A man is `ENEMY.radius`; a
 * machine is not, and testing one as though it were is why a LAW fired at a minitank sailed straight
 * through it - its tracks span +-0.79 m and the test circle was 0.4 m, so a rocket that visibly hit
 * the hull registered nothing and flew on. A vehicle is longer than it is wide and is answered by
 * its capsule (`hullSpine`), not by this.
 */
export function hullRadius(kind: EnemyKind): number {
  if (kind === 'minitank') return SPECIALS.tankBody;
  if (kind === 'miniBunker') return SPECIALS.bunkerBody;
  if (isVehicle(kind)) return VEHICLES.radius;
  return ENEMY.radius;
}

/** Metres per second over the last tick, from the interpolation pair every mover keeps. */
export function speedOf(m: { x: number; z: number; prevX: number; prevZ: number }): number {
  return Math.sqrt((m.x - m.prevX) * (m.x - m.prevX) + (m.z - m.prevZ) * (m.z - m.prevZ)) * TICK_RATE;
}

/**
 * A machine is wrecked (or a man is lost outright): no wound roll, no knock-down. The wreck
 * is marked `found` so no sentry ever "discovers a body" and raises the alert over it.
 */
export function destroyOutright(ctx: Ctx, e: Enemy, byPlayer: boolean): void {
  if (e.state === 'dead') return;
  e.state = 'dead';
  e.hp = 0;
  e.moving = false;
  e.peeking = false;
  e.burstLeft = 0;
  e.found = true;
  e.path = [];
  if (byPlayer) ctx.world.ranger.kills++;
  ctx.events.push({ type: 'enemyHit', id: e.id, killed: true });
}

/** Armour forgets every scratch: called each tick so carbine fire and fragments never add up. */
export function restoreArmour(e: Enemy): void {
  e.hp = SPECIALS.armourHp;
  // `damageEnemy` knocks men 'down' and points them at the ranger; a machine is simply always 'down'
  // to the infantry's code, which therefore never hears through it, rallies to it or takes prisoners with it.
  e.state = 'down';
  e.detection = 0;
  e.seesRanger = false;
}

/** The memo for this flamethrower's fuel tank, made on first sight. */
export function tankOf(h: HazardState, e: Enemy): FuelTank {
  let tank = h.fuelTanks.find((t) => t.enemyId === e.id);
  if (!tank) {
    tank = { enemyId: e.id, intact: true, hp: e.hp, fuseTicks: -1 };
    h.fuelTanks.push(tank);
  }
  return tank;
}
